import { useState, useEffect, useRef } from '@wordpress/element';
import { Modal, Button, TextControl } from '@wordpress/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as SolidIcons from '@fortawesome/free-solid-svg-icons';
import * as BrandIcons from '@fortawesome/free-brands-svg-icons';
import * as RegularIcons from '@fortawesome/free-regular-svg-icons';
import { FixedSizeGrid as Grid, VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import './fontawesome.css';

const FontAwesomeModal = ({ isOpen, onRequestClose, onSelectIcon }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [tab, setTab] = useState('all');
    const [solidIcons, setSolidIcons] = useState([]);
    const [brandIcons, setBrandIcons] = useState([]);
    const [regularIcons, setRegularIcons] = useState([]);
    const [filteredSolidIcons, setFilteredSolidIcons] = useState([]);
    const [filteredBrandIcons, setFilteredBrandIcons] = useState([]);
    const [filteredRegularIcons, setFilteredRegularIcons] = useState([]);
    const debounceTimeoutRef = useRef(null);
    const gridRef = useRef(null);

    const [gridDimensions, setGridDimensions] = useState({ columnCount: 8, columnWidth: 60 });

    useEffect(() => {
        const handleResize = () => {
            if (gridRef.current) {
                const containerWidth = gridRef.current.clientWidth;
                const columnCount = 8;
                const columnWidth = containerWidth / columnCount;
                setGridDimensions({ columnCount, columnWidth });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const solidIconKeys = Object.keys(SolidIcons).filter(key => key.startsWith('fa'));
        const brandIconKeys = Object.keys(BrandIcons).filter(key => key.startsWith('fa'));
        const regularIconKeys = Object.keys(RegularIcons).filter(key => key.startsWith('fa'));
        setSolidIcons(solidIconKeys);
        setBrandIcons(brandIconKeys);
        setRegularIcons(regularIconKeys)
    }, []);

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
    }, [searchTerm]);

    // Lọc icon theo debouncedSearchTerm
    useEffect(() => {
        const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
        setFilteredSolidIcons(
            solidIcons.filter(key => key.toLowerCase().includes(lowerSearchTerm))
        );
        setFilteredBrandIcons(
            brandIcons.filter(key => key.toLowerCase().includes(lowerSearchTerm))
        );
        setFilteredRegularIcons(
            regularIcons.filter(key => key.toLowerCase().includes(lowerSearchTerm))
        );
    }, [debouncedSearchTerm, solidIcons, brandIcons, regularIcons]);

    const iconsToShow = (tab === 'all') ? [...filteredSolidIcons, ...filteredBrandIcons, ...filteredRegularIcons] :
        (tab === 'solid') ? filteredSolidIcons : (tab === 'brand') ?  filteredBrandIcons : filteredRegularIcons;

    const Cell = ({ columnIndex, rowIndex, style }) => {
        const index = rowIndex * gridDimensions.columnCount + columnIndex;
        const iconKey = iconsToShow[index];
        if (!iconKey) return null;

        const icon = solidIcons.includes(iconKey) ? SolidIcons[iconKey] : 
                      brandIcons.includes(iconKey) ? BrandIcons[iconKey] : 
                      RegularIcons[iconKey];
        
        return (
            <div style={style}>
                <Button className='softlite-button-select-icon' onClick={() => onSelectIcon(icon)} style={{ width: '100%', height: '100%' }}>
                    <FontAwesomeIcon icon={icon} style={{ width: '20px', height: '20px' }} />
                    <span style={{ fontSize: '11px', marginTop: '5px' }}>{iconKey.replace('fa', '').replace(/([A-Z])/g, ' $1').trim()}</span>
                </Button>
            </div>
        );
    };

    const isItemLoaded = index => !!iconsToShow[index];

    const loadMoreItems = (startIndex, stopIndex) => {
        return new Promise(resolve => resolve());
    };

    return (
        <Modal title="Select Font Awesome Icon" onRequestClose={onRequestClose} isOpen={isOpen} size="large">
            <TextControl
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
                placeholder='Search Icon'
            />
            <div className='softlite-fortawesome-box'>
                <div className="softlite-fortawesome-box__left">
                    <Button className={`softlite-button-select-category${tab == 'all' ? ' softlite-active' : ''}`} onClick={() => setTab('all')}>All</Button>
                    <Button className={`softlite-button-select-category${tab == 'brand' ? ' softlite-active' : ''}`} onClick={() => setTab('brand')}>Brand Icons</Button>
                    <Button className={`softlite-button-select-category${tab == 'solid' ? ' softlite-active' : ''}`} onClick={() => setTab('solid')}>Solid Icons</Button>
                    <Button className={`softlite-button-select-category${tab == 'regular' ? ' softlite-active' : ''}`} onClick={() => setTab('regular')}>Regular Icons</Button>
                </div>
                <div className="softlite-fortawesome-box__right" ref={gridRef}>
                    <InfiniteLoader
                        isItemLoaded={isItemLoaded}
                        itemCount={iconsToShow.length}
                        loadMoreItems={loadMoreItems}
                    >
                        {({ onItemsRendered, ref }) => (
                            <Grid
                                columnCount={gridDimensions.columnCount}
                                columnWidth={gridDimensions.columnWidth}
                                height={400}
                                rowCount={Math.ceil(iconsToShow.length / gridDimensions.columnCount)}
                                rowHeight={80}
                                width={gridRef.current ? gridRef.current.clientWidth : 500}
                                onItemsRendered={onItemsRendered}
                                ref={ref}
                            >
                                {Cell}
                            </Grid>
                        )}
                    </InfiniteLoader>
                </div>
            </div>
        </Modal>
    );
};

export default FontAwesomeModal;